import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, 
  Chrome, 
  Monitor, 
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Settings,
  ChevronRight,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import config from '../../config/env';

const VideoPlayer = ({ src, poster, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(!src);

  const handlePlayPause = () => {
    const video = document.getElementById('installation-video');
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMute = () => {
    const video = document.getElementById('installation-video');
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    const video = document.getElementById('installation-video');
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    }
  };

  if (showPlaceholder || !src) {
    return (
      <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
        {/* Placeholder Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full border-2 border-white/20 flex items-center justify-center mb-6 group-hover:border-accent/50 transition-colors">
            <Play className="h-8 w-8 text-white/40 group-hover:text-accent transition-colors ml-1" />
          </div>
          <p className="text-white/40 text-sm font-mono tracking-wide">{title || 'VIDEO COMING SOON'}</p>
          <p className="text-white/20 text-xs mt-2">Installation tutorial will be available here</p>
        </div>
        
        {/* Grid Pattern Background */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
      <video
        id="installation-video"
        className="w-full h-full object-contain bg-black"
        poster={poster}
        onEnded={() => setIsPlaying(false)}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Video Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={handlePlayPause}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 text-white" />
              ) : (
                <Play className="h-5 w-5 text-white ml-0.5" />
              )}
            </button>
            <button 
              onClick={handleMute}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5 text-white" />
              ) : (
                <Volume2 className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
          <button 
            onClick={handleFullscreen}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <Maximize className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={handlePlayPause}
        >
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
            <Play className="h-8 w-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

const Installation = () => {
  const installSteps = [
    {
      num: '01',
      title: 'Download the Extension',
      description: 'Click the download button to get the latest version of the NeoPass extension as a ZIP file.',
      status: 'required'
    },
    {
      num: '02',
      title: 'Extract the ZIP File',
      description: 'Locate the downloaded file and extract it to a folder on your computer.',
      status: 'required'
    },
    {
      num: '03',
      title: 'Open Extensions Page',
      description: 'Go to chrome://extensions in Chrome, edge://extensions in Edge, or brave://extensions in Brave.',
      status: 'required'
    },
    {
      num: '04',
      title: 'Enable Developer Mode',
      description: 'Toggle on "Developer mode" in the top-right corner of the extensions page.',
      status: 'required'
    },
    {
      num: '05',
      title: 'Load Unpacked Extension',
      description: 'Click "Load unpacked" and select the extracted extension folder.',
      status: 'required'
    },
    {
      num: '06',
      title: 'Pin the Extension',
      description: 'Click the puzzle icon in your browser toolbar and pin NeoPass for easy access.',
      status: 'optional'
    },
  ];

  const browsers = [
    { name: 'Chrome', icon: Chrome, supported: true },
    { name: 'Edge', icon: Monitor, supported: true },
    { name: 'Brave', icon: Monitor, supported: true },
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">SETUP</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Installation Guide
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Follow these steps to install the NeoPass browser extension and start your learning journey.
          </p>
        </div>
      </section>

      {/* Video Tutorial Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">VIDEO TUTORIAL</span>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
                Watch the installation walkthrough
              </h2>
              <p className="text-white/50 mb-6">
                This video demonstrates the complete installation process step by step. Follow along to get NeoPass set up in minutes.
              </p>
              
              {/* Browser Support */}
              <div className="flex flex-wrap gap-4 mb-8">
                {browsers.map((browser, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg"
                  >
                    <browser.icon className="h-4 w-4 text-white/60" />
                    <span className="text-white/70 text-sm">{browser.name}</span>
                    {browser.supported && (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    )}
                  </div>
                ))}
              </div>

              <a 
                href={config.extensionDownloadUrl}
                target="_blank"
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors"
              >
                <Download className="h-5 w-5" />
                DOWNLOAD EXTENSION
              </a>
            </div>

            {/* Video Player */}
            <VideoPlayer 
              src={config.videos.installation}
              poster=""
              title="INSTALLATION TUTORIAL"
            />
          </div>
        </div>
      </section>

      {/* Step by Step Guide */}
      <section className="py-12 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">STEP BY STEP</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-10">
            Installation steps
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {installSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-[#0a0a0a] p-8 group hover:bg-white/[0.02] transition-all duration-500"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-light text-white/20 group-hover:text-accent/50 transition-colors font-mono">
                    {step.num}
                  </span>
                  {step.status === 'optional' && (
                    <span className="text-xs font-mono text-white/30 px-2 py-1 border border-white/10 rounded">
                      OPTIONAL
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-medium text-white mb-2 group-hover:text-accent transition-colors">
                  {step.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">TROUBLESHOOTING</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">
            Common issues
          </h2>
          
          <div className="space-y-4">
            <div className="p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <h4 className="text-white font-medium mb-2">Extension not loading?</h4>
                  <p className="text-white/50 text-sm">Make sure Developer Mode is enabled and you've selected the correct folder containing the manifest.json file.</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <h4 className="text-white font-medium mb-2">Getting errors after update?</h4>
                  <p className="text-white/50 text-sm">Remove the old extension completely before loading the new version. Then reload the extension from the extensions page.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-light text-white mb-2">
                Ready to get started?
              </h2>
              <p className="text-white/50">
                Once installed, check out our usage guide to make the most of NeoPass.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/guide"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors"
              >
                View Usage Guide
                <ChevronRight className="h-4 w-4" />
              </Link>
              <Link 
                to="/signup"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
              >
                Create Account
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default Installation;
